import React, { createContext, useContext } from 'react';
import cctcLogo from 'assets/images/cctc-logo.png';
import cctcLogoDark from 'assets/images/cctc-logo-dark.png';

/**
 * White-label CourseConfig — per-course branding for the shared authed app, resolved at
 * runtime. One codebase re-skins per course; adding a course = a new config entry (+ assets),
 * not a fork. (The public marketing sites are WordPress and out of scope — this covers the
 * authenticated app only.)
 *
 * For now there is a single course (cardiac-ct); the active course is chosen by
 * VITE_APP_COURSE_SLUG, defaulting to cardiac-ct. Phase 5's hub will switch it per route.
 * Static frontend config for now; sourcing the data-only bits from the backend Course
 * (brandConfig/dicomConfig/accreditationBodies) is a later option.
 */

// Question types this app knows how to render. A course declares which subset it uses; the
// CasePage renders only those. New specialty = config (+ at most one new component).
export const ALL_QUESTION_TYPES = [
    'MultiChoiceQuestion',
    'PredictiveQuestion',
    'SvgQuestion',
    'FreeTextQuestion'
] as const;
export type QuestionType = (typeof ALL_QUESTION_TYPES)[number];

export interface CourseConfig {
    slug: string;
    displayName: string;
    // Logo asset URLs (Vite resolves the imports to hashed URLs).
    logo: { light: string; dark: string };
    // Brand colours fed into the MUI/styled-components themes. The non-brand palette
    // (error/warning/info/etc.) stays shared in config/mui-theme.ts.
    theme: {
        primary: { main: string; light: string; dark: string };
        secondary: { main: string; light: string; dark: string };
    };
    dicom: {
        baseUrl: string;
    };
    logbook: {
        exportFileName: string;
        submissionIdLabel: string;
    };
    // Where "contact us" prompts (payment/subscription help) point.
    supportEmail: string;
    // The public-demo account this course exposes (a fixed logged-in demo user).
    demo: {
        email: string;
    };
    // Question types this course uses (drives the CasePage render registry).
    questionTypes: QuestionType[];
}

const cardiacCt: CourseConfig = {
    slug: 'cardiac-ct',
    displayName: 'The Cardiac CT Course',
    logo: { light: cctcLogo, dark: cctcLogoDark },
    theme: {
        primary: { main: 'rgb(173, 13, 45)', light: '#ed2f55', dark: '#002c52' },
        secondary: { main: '#7b0a20', light: '#61e18d', dark: '#007d33' }
    },
    dicom: {
        baseUrl: import.meta.env.VITE_APP_DICOM_BASE_URL
    },
    logbook: {
        exportFileName: 'TheCardiacCTCourse-Logbook.xlsx',
        submissionIdLabel: 'Cardiac CT Course Submission ID'
    },
    supportEmail: 'admin@thecardiacctcourse.com',
    demo: {
        email: import.meta.env.VITE_APP_DEMO_USER_EMAIL
    },
    questionTypes: ['MultiChoiceQuestion', 'PredictiveQuestion', 'SvgQuestion', 'FreeTextQuestion']
};

const COURSE_CONFIGS: Record<string, CourseConfig> = {
    [cardiacCt.slug]: cardiacCt
};

const DEFAULT_COURSE_SLUG = 'cardiac-ct';

/**
 * The active course config. Usable outside React (themes, utils) — the React tree should
 * prefer the `useCourseConfig` hook so a future hub can switch courses via the provider.
 */
export const getCurrentCourseConfig = (): CourseConfig => {
    const slug = import.meta.env.VITE_APP_COURSE_SLUG ?? DEFAULT_COURSE_SLUG;
    return COURSE_CONFIGS[slug] ?? COURSE_CONFIGS[DEFAULT_COURSE_SLUG];
};

const CourseConfigContext = createContext<CourseConfig>(getCurrentCourseConfig());

export const CourseConfigProvider = ({
    config = getCurrentCourseConfig(),
    children
}: {
    config?: CourseConfig;
    children: React.ReactNode;
}) => <CourseConfigContext.Provider value={config}>{children}</CourseConfigContext.Provider>;

export const useCourseConfig = (): CourseConfig => useContext(CourseConfigContext);
